// [cursor 2026-06-01] S87-T4 finish-up: compact right-justified RiskFlow mode control.
// Replaces the X status light + Proposals button on RiskFlow surfaces.
import { useEffect, useRef, useState } from "react";
import { Activity, Bookmark, ChevronDown, ShieldAlert } from "lucide-react";
import { cn } from "../../lib/utils";

export type RiskFlowMode = "catalysts" | "risk-signals" | "bookmarks";

const MODE_OPTIONS: {
  id: RiskFlowMode;
  label: string;
  icon: typeof Activity;
}[] = [
  { id: "catalysts", label: "Catalysts", icon: Activity },
  { id: "risk-signals", label: "Risk Signals", icon: ShieldAlert },
  { id: "bookmarks", label: "Bookmarks", icon: Bookmark },
];

interface RiskFlowModeControlProps {
  mode: RiskFlowMode;
  onChange: (mode: RiskFlowMode) => void;
  className?: string;
}

export function RiskFlowModeControl({
  mode,
  onChange,
  className,
}: RiskFlowModeControlProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active =
    MODE_OPTIONS.find((option) => option.id === mode) ?? MODE_OPTIONS[0];
  const ActiveIcon = active.icon;

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex h-7 items-center gap-1.5 rounded-md border border-[var(--fintheon-accent)]/24 px-2.5 text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--fintheon-accent)] transition-colors hover:bg-[var(--fintheon-accent)]/8"
      >
        <ActiveIcon size={12} />
        <span>{active.label}</span>
        <ChevronDown size={12} className={open ? "rotate-180" : undefined} />
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute right-0 z-30 mt-1 w-40 overflow-hidden rounded-md border border-[var(--fintheon-accent)]/20 bg-[color-mix(in_srgb,var(--fintheon-accent)_6%,rgba(5,4,2,0.94))] py-1 backdrop-blur-xl"
        >
          {MODE_OPTIONS.map((option) => {
            const Icon = option.icon;
            const selected = option.id === mode;
            return (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(option.id);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-1.5 text-[11px] transition-colors",
                  selected
                    ? "text-[var(--fintheon-accent)]"
                    : "text-[var(--fintheon-text)]/55 hover:bg-[var(--fintheon-accent)]/6 hover:text-[var(--fintheon-text)]/85",
                )}
              >
                <Icon size={12} />
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
